import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, FolderKanban, Ticket, Activity,
  Bell, Server, Cpu, Shield, AlertTriangle,
  Plus, RefreshCw, LogOut, User, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, token, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', priority: 'medium' });

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/login');
    } else if (token) {
      fetchDashboardData();
    }
  }, [token, authLoading, navigate]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, projectsRes, ticketsRes, healthRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/projects`),
        axios.get(`${API}/dashboard/tickets`),
        axios.get(`${API}/dashboard/system-health`)
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setTickets(ticketsRes.data);
      setSystemHealth(healthRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/dashboard/tickets`, newTicket);
      setTickets([response.data, ...tickets]);
      setNewTicket({ subject: '', description: '', priority: 'medium' });
      setNewTicketOpen(false);
      toast.success('Support ticket created');
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-background">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const sidebarItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'projects', icon: FolderKanban, label: 'Projects' },
    { id: 'tickets', icon: Ticket, label: 'Support' },
    { id: 'system', icon: Activity, label: 'System Health' },
  ];

  return (
    <div data-testid="dashboard-page" className="min-h-screen pt-16 bg-[#05050A]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-64px)] bg-card/30 border-r border-white/10 p-4 hidden lg:block">
          <div className="mb-8">
            <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10">
              <div className="w-10 h-10 bg-primary/20 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div className="overflow-hidden">
                <div className="font-medium truncate">{user.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              </div>
            </div>
          </div>

          <nav className="space-y-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${
                  activeTab === item.id
                    ? 'bg-primary/10 text-primary border-l-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
                data-testid={`sidebar-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="mt-auto pt-8">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-destructive transition-colors"
              data-testid="sidebar-logout"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {/* Mobile Tab Navigation */}
          <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto pb-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap ${
                  activeTab === item.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card/30 text-muted-foreground'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="mb-8">
                <h1 className="font-heading text-3xl font-bold mb-2">
                  Welcome back, {user.name.split(' ')[0]}
                </h1>
                <p className="text-muted-foreground">
                  Here&apos;s an overview of your SentinelX dashboard
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Active Projects', value: stats?.active_projects || 0, icon: FolderKanban, color: 'primary' },
                  { label: 'Open Tickets', value: stats?.open_tickets || 0, icon: Ticket, color: 'secondary' },
                  { label: 'Active Nodes', value: stats?.active_nodes || 0, icon: Server, color: 'primary' },
                  { label: 'System Uptime', value: `${stats?.system_uptime || 0}%`, icon: Activity, color: 'primary' },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card/30 border border-white/10 p-4"
                    data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <stat.icon className={`w-5 h-5 text-${stat.color}`} />
                      {stat.label === 'System Uptime' && (
                        <Badge variant="outline" className="text-xs border-green-500/30 text-green-500">
                          Healthy
                        </Badge>
                      )}
                    </div>
                    <div className="font-heading text-3xl font-bold">{stat.value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Quick Actions & Recent */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Recent Projects */}
                <div className="bg-card/30 border border-white/10 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold">Recent Projects</h3>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setActiveTab('projects')}
                    >
                      View All
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {projects.slice(0, 3).map((project) => (
                      <div 
                        key={project.id}
                        className="flex items-center gap-4 p-3 bg-white/5 border border-white/5"
                      >
                        <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                          <FolderKanban className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{project.name}</div>
                          <div className="text-xs text-muted-foreground">{project.service_type}</div>
                        </div>
                        <Badge 
                          variant="outline"
                          className={project.status === 'active' ? 'border-green-500/30 text-green-500' : ''}
                        >
                          {project.status}
                        </Badge>
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No projects yet</p>
                    )}
                  </div>
                </div>

                {/* System Health Preview */}
                <div className="bg-card/30 border border-white/10 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold">System Health</h3>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setActiveTab('system')}
                    >
                      Details
                    </Button>
                  </div>
                  {systemHealth && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">CPU Usage</span>
                          <span>{systemHealth.cpu_usage}%</span>
                        </div>
                        <Progress value={systemHealth.cpu_usage} className="h-2" />
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Memory Usage</span>
                          <span>{systemHealth.memory_usage}%</span>
                        </div>
                        <Progress value={systemHealth.memory_usage} className="h-2" />
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="text-center p-3 bg-white/5">
                          <div className="text-2xl font-bold text-primary">{systemHealth.active_nodes}</div>
                          <div className="text-xs text-muted-foreground">Active Nodes</div>
                        </div>
                        <div className="text-center p-3 bg-white/5">
                          <div className="text-2xl font-bold text-yellow-500">{systemHealth.alerts}</div>
                          <div className="text-xs text-muted-foreground">Alerts</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold mb-2">Projects</h1>
                  <p className="text-muted-foreground">Manage your active projects</p>
                </div>
              </div>

              <div className="grid gap-4">
                {projects.map((project) => (
                  <div 
                    key={project.id}
                    className="bg-card/30 border border-white/10 p-6"
                    data-testid={`project-${project.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-xl font-semibold">{project.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
                      </div>
                      <Badge 
                        variant="outline"
                        className={project.status === 'active' ? 'border-green-500/30 text-green-500' : ''}
                      >
                        {project.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-2">
                        <Cpu className="w-4 h-4" />
                        {project.service_type}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {new Date(project.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} className="h-2" />
                    </div>
                  </div>
                ))}
                {projects.length === 0 && (
                  <div className="text-center py-12 bg-card/20 border border-white/10">
                    <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No projects found</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold mb-2">Support Tickets</h1>
                  <p className="text-muted-foreground">Get help from our support team</p>
                </div>
                <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-primary text-primary-foreground"
                      data-testid="create-ticket-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Ticket
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Support Ticket</DialogTitle>
                      <DialogDescription>
                        Describe your issue and our team will respond shortly.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Subject</Label>
                        <Input
                          placeholder="Brief description of the issue"
                          value={newTicket.subject}
                          onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                          data-testid="ticket-subject-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select 
                          value={newTicket.priority} 
                          onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                        >
                          <SelectTrigger data-testid="ticket-priority-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          placeholder="Detailed description of your issue..."
                          rows={4}
                          value={newTicket.description}
                          onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                          data-testid="ticket-description-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setNewTicketOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateTicket} data-testid="submit-ticket-btn">
                        Create Ticket
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div 
                    key={ticket.id}
                    className="bg-card/30 border border-white/10 p-6"
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-heading font-semibold">{ticket.subject}</h3>
                      <div className="flex gap-2">
                        <Badge 
                          variant="outline"
                          className={
                            ticket.priority === 'high' ? 'border-red-500/30 text-red-500' :
                            ticket.priority === 'medium' ? 'border-yellow-500/30 text-yellow-500' :
                            'border-white/30'
                          }
                        >
                          {ticket.priority}
                        </Badge>
                        <Badge 
                          variant="outline"
                          className={ticket.status === 'open' ? 'border-green-500/30 text-green-500' : ''}
                        >
                          {ticket.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{ticket.description}</p>
                    <div className="text-xs text-muted-foreground">
                      Created: {new Date(ticket.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
                {tickets.length === 0 && (
                  <div className="text-center py-12 bg-card/20 border border-white/10">
                    <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No support tickets</p>
                    <Button onClick={() => setNewTicketOpen(true)}>
                      Create Your First Ticket
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* System Health Tab */}
          {activeTab === 'system' && systemHealth && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold mb-2">System Health</h1>
                  <p className="text-muted-foreground">Monitor your infrastructure status</p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchDashboardData}
                  data-testid="refresh-health-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card/30 border border-white/10 p-6 text-center">
                  <Server className="w-10 h-10 text-primary mx-auto mb-4" />
                  <div className="text-4xl font-bold mb-2">
                    {systemHealth.active_nodes}/{systemHealth.total_nodes}
                  </div>
                  <div className="text-muted-foreground">Active Nodes</div>
                </div>
                <div className="bg-card/30 border border-white/10 p-6 text-center">
                  <Activity className="w-10 h-10 text-green-500 mx-auto mb-4" />
                  <div className="text-4xl font-bold mb-2">{systemHealth.uptime}%</div>
                  <div className="text-muted-foreground">Uptime</div>
                </div>
                <div className="bg-card/30 border border-white/10 p-6 text-center">
                  <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                  <div className="text-4xl font-bold mb-2">{systemHealth.alerts}</div>
                  <div className="text-muted-foreground">Active Alerts</div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card/30 border border-white/10 p-6">
                  <h3 className="font-heading font-semibold mb-6">Resource Usage</h3>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2">
                          <Cpu className="w-4 h-4 text-primary" />
                          CPU
                        </span>
                        <span className="font-mono">{systemHealth.cpu_usage}%</span>
                      </div>
                      <Progress value={systemHealth.cpu_usage} className="h-3" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2">
                          <Server className="w-4 h-4 text-secondary" />
                          Memory
                        </span>
                        <span className="font-mono">{systemHealth.memory_usage}%</span>
                      </div>
                      <Progress value={systemHealth.memory_usage} className="h-3" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-green-500" />
                          Network I/O
                        </span>
                        <span className="font-mono">{systemHealth.network_io} MB/s</span>
                      </div>
                      <Progress value={(systemHealth.network_io / 500) * 100} className="h-3" />
                    </div>
                  </div>
                </div>

                <div className="bg-card/30 border border-white/10 p-6">
                  <h3 className="font-heading font-semibold mb-6">Security Status</h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Firewall', status: 'Active', healthy: true },
                      { label: 'Intrusion Detection', status: 'Monitoring', healthy: true },
                      { label: 'SSL Certificates', status: 'Valid', healthy: true },
                      { label: 'Backup Status', status: 'Up to date', healthy: true },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between p-3 bg-white/5">
                        <div className="flex items-center gap-3">
                          <Shield className={`w-5 h-5 ${item.healthy ? 'text-green-500' : 'text-red-500'}`} />
                          <span>{item.label}</span>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={item.healthy ? 'border-green-500/30 text-green-500' : 'border-red-500/30 text-red-500'}
                        >
                          {item.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
}
